import { useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CopyIcon,
  GiftIcon,
  Loader2Icon,
  PencilIcon,
  PlusIcon,
  RefreshCwIcon,
  SearchIcon,
  Trash2Icon,
} from 'lucide-react'
import { toast } from 'sonner'

import { CreditTypeSelect } from '@/components/admin/credit-settings'
import { REGULAR_CREDIT_TYPE } from '@/lib/credits-api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  createVoucherCode,
  deleteVoucherCode,
  generateVoucherCode,
  getVoucherCodes,
  updateVoucherCode,
} from '@/lib/api'
import { DEFAULT_TABLE_PAGE_SIZE, TABLE_PAGE_SIZE_OPTIONS } from '@/lib/constants'
import { formatDateTime } from '@/lib/date'
import { getErrorMessage } from '@/lib/error-messages'
import { queryKeys } from '@/lib/query-keys'
import { formatCreditAmount, parseCreditAmount } from '@/lib/subscriptions'
import type { VoucherCode } from '@/types/domain'

type DialogMode = 'create' | 'edit'

function voucherStatus(voucher: { remaining_redemptions: number }) {
  if (voucher.remaining_redemptions <= 0) {
    return { label: 'Aufgebraucht', variant: 'destructive' as const }
  }

  return { label: 'Aktiv', variant: 'default' as const }
}

function parsePositiveInteger(value: string): number | null {
  const parsed = Math.trunc(Number(value))
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null
  }

  return parsed
}

export function AdminVouchersPage() {
  const queryClient = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<DialogMode>('create')
  const [editingVoucher, setEditingVoucher] = useState<VoucherCode | null>(null)
  const [code, setCode] = useState('')
  const [credits, setCredits] = useState('1')
  const [creditTypeId, setCreditTypeId] = useState(REGULAR_CREDIT_TYPE)
  const [maxRedemptions, setMaxRedemptions] = useState('1')
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [voucherToDelete, setVoucherToDelete] = useState<VoucherCode | null>(null)

  const vouchersQuery = useQuery({
    queryKey: queryKeys.vouchers,
    queryFn: getVoucherCodes,
  })

  const generatedCodeMutation = useMutation({
    mutationKey: queryKeys.voucherGeneratedCode,
    mutationFn: generateVoucherCode,
    onSuccess: (generatedCode) => {
      setCode(generatedCode)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const createMutation = useMutation({
    mutationFn: createVoucherCode,
    onSuccess: (voucher) => {
      closeDialog()
      queryClient.invalidateQueries({ queryKey: queryKeys.vouchers })
      toast.success(`Gutschein ${voucher.code} wurde erstellt.`)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const updateMutation = useMutation({
    mutationFn: updateVoucherCode,
    onSuccess: (voucher) => {
      closeDialog()
      queryClient.invalidateQueries({ queryKey: queryKeys.vouchers })
      toast.success(`Gutschein ${voucher.code} wurde aktualisiert.`)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteVoucherCode,
    onSuccess: () => {
      setDeleteConfirmOpen(false)
      setVoucherToDelete(null)
      queryClient.invalidateQueries({ queryKey: queryKeys.vouchers })
      toast.success('Gutschein wurde gelöscht.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  function resetDialogState() {
    setEditingVoucher(null)
    setCode('')
    setCredits('1')
    setCreditTypeId(REGULAR_CREDIT_TYPE)
    setMaxRedemptions('1')
  }

  function closeDialog() {
    setDialogOpen(false)
    resetDialogState()
  }

  function openCreateDialog() {
    resetDialogState()
    setDialogMode('create')
    setDialogOpen(true)
  }

  function openEditDialog(voucher: VoucherCode) {
    setEditingVoucher(voucher)
    setDialogMode('edit')
    setCode(voucher.code)
    setCredits(String(voucher.credits))
    setCreditTypeId(voucher.credit_type_id ?? REGULAR_CREDIT_TYPE)
    setMaxRedemptions(String(voucher.max_redemptions))
    setDialogOpen(true)
  }

  function openDeleteConfirm(voucher: VoucherCode) {
    setVoucherToDelete(voucher)
    setDeleteConfirmOpen(true)
  }

  function handleDialogOpenChange(open: boolean) {
    if (createMutation.isPending || updateMutation.isPending || generatedCodeMutation.isPending) {
      return
    }

    if (!open) {
      closeDialog()
      return
    }

    setDialogOpen(true)
  }

  function handleGenerateCode() {
    generatedCodeMutation.mutate()
  }

  function handleSubmitVoucher(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    const parsedCredits = parseCreditAmount(credits)
    const parsedMaxRedemptions = parsePositiveInteger(maxRedemptions)
    const normalizedCode = code.trim()

    if (!normalizedCode) {
      toast.error(getErrorMessage(new Error('VOUCHER_CODE_INVALID')))
      return
    }

    if (parsedCredits === null) {
      toast.error(getErrorMessage(new Error('VOUCHER_CREDITS_INVALID')))
      return
    }

    if (parsedMaxRedemptions === null) {
      toast.error(getErrorMessage(new Error('VOUCHER_REDEMPTIONS_INVALID')))
      return
    }

    if (dialogMode === 'edit' && editingVoucher) {
      const fieldsLocked = editingVoucher.redeemed_count > 0

      updateMutation.mutate({
        id: editingVoucher.id,
        creditTypeId,
        code: fieldsLocked ? editingVoucher.code : normalizedCode,
        credits: fieldsLocked ? editingVoucher.credits : parsedCredits,
        maxRedemptions: parsedMaxRedemptions,
      })
      return
    }

    createMutation.mutate({
      code: normalizedCode,
      credits: parsedCredits,
      creditTypeId,
      maxRedemptions: parsedMaxRedemptions,
    })
  }

  async function copyCode(codeToCopy: string) {
    try {
      await navigator.clipboard.writeText(codeToCopy)
      toast.success('Code kopiert.')
    } catch {
      toast.error('Code konnte nicht kopiert werden.')
    }
  }

  const vouchers = useMemo(() => vouchersQuery.data ?? [], [vouchersQuery.data])
  const filteredVouchers = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase()

    if (!normalizedSearch) {
      return vouchers
    }

    return vouchers.filter((voucher) => {
      const status = voucherStatus(voucher).label.toLowerCase()
      return (
        voucher.code.toLowerCase().includes(normalizedSearch) ||
        status.includes(normalizedSearch) ||
        String(voucher.credits).includes(normalizedSearch) ||
        String(voucher.max_redemptions).includes(normalizedSearch)
      )
    })
  }, [searchTerm, vouchers])
  const totalVouchers = filteredVouchers.length
  const totalPages = Math.max(1, Math.ceil(totalVouchers / pageSize))
  const currentPage = Math.min(page, totalPages)
  const paginatedVouchers = filteredVouchers.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  )
  const saving = createMutation.isPending || updateMutation.isPending
  const redeemedFieldsLocked = dialogMode === 'edit' && (editingVoucher?.redeemed_count ?? 0) > 0
  const dialogTitle = dialogMode === 'edit' ? 'Gutschein bearbeiten' : 'Gutschein erstellen'
  const dialogDescription =
    dialogMode === 'edit'
      ? 'Passe Code, Credits oder maximale Einlösungen für zukünftige Einlösungen an.'
      : 'Lege einen Gutschein mit manuellem oder generiertem Code an.'

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Gutscheine</h1>
          <p className="text-sm text-muted-foreground">
            Erstelle und verwalte Gutschein-Codes für Kursguthaben.
          </p>
        </div>
        <Button type="button" onClick={openCreateDialog}>
          <PlusIcon className="mr-2 h-4 w-4" />
          Gutschein erstellen
        </Button>
      </div>

      <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription>{dialogDescription}</DialogDescription>
          </DialogHeader>

          <form className="space-y-5" onSubmit={handleSubmitVoucher}>
            <div className="space-y-2">
              <Label htmlFor="voucher-code">Code</Label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  id="voucher-code"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  placeholder="SPRING-TEST-3"
                  disabled={saving || generatedCodeMutation.isPending || redeemedFieldsLocked}
                  autoComplete="off"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGenerateCode}
                  disabled={saving || generatedCodeMutation.isPending || redeemedFieldsLocked}
                >
                  {generatedCodeMutation.isPending ? (
                    <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCwIcon className="mr-2 h-4 w-4" />
                  )}
                  Generieren
                </Button>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <CreditTypeSelect value={creditTypeId} onChange={setCreditTypeId} disabled={Boolean(editingVoucher?.redeemed_count)} />
                <Label htmlFor="voucher-credits">Credits</Label>
                <Input
                  id="voucher-credits"
                  type="number"
                  min={0.1}
                  step={0.1}
                  value={credits}
                  onChange={(event) => setCredits(event.target.value)}
                  disabled={saving || redeemedFieldsLocked}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="voucher-redemptions">Max. Einlösungen</Label>
                <Input
                  id="voucher-redemptions"
                  type="number"
                  min={1}
                  step={1}
                  value={maxRedemptions}
                  onChange={(event) => setMaxRedemptions(event.target.value)}
                  disabled={saving}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog} disabled={saving}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={saving || generatedCodeMutation.isPending}>
                {saving ? (
                  <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                ) : dialogMode === 'edit' ? (
                  <PencilIcon className="mr-2 h-4 w-4" />
                ) : (
                  <GiftIcon className="mr-2 h-4 w-4" />
                )}
                Speichern
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onOpenChange={(open) => {
          setDeleteConfirmOpen(open)
          if (!open && !deleteMutation.isPending) {
            setVoucherToDelete(null)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gutschein löschen?</DialogTitle>
            <DialogDescription>
              Dieser Gutschein wird endgültig gelöscht. Das ist nur möglich, solange er noch nicht eingelöst wurde.
            </DialogDescription>
          </DialogHeader>

          {voucherToDelete ? (
            <div className="rounded-md border p-3 text-sm">
              <span className="text-muted-foreground">Code: </span>
              <span className="font-mono font-medium">{voucherToDelete.code}</span>
            </div>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDeleteConfirmOpen(false)
                setVoucherToDelete(null)
              }}
              disabled={deleteMutation.isPending}
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                if (voucherToDelete) {
                  deleteMutation.mutate(voucherToDelete.id)
                }
              }}
              disabled={deleteMutation.isPending || !voucherToDelete}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                  Löscht ...
                </>
              ) : (
                'Ja, löschen'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="min-h-0 overflow-hidden">
        <CardHeader>
          <CardTitle className="text-lg">Gutschein-Codes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-[1fr_180px]">
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Nach Code, Status oder Credits suchen ..."
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value)
                  setPage(1)
                }}
              />
            </div>
            <Select
              value={String(pageSize)}
              onValueChange={(value) => {
                setPageSize(Number(value))
                setPage(1)
              }}
            >
              <SelectTrigger className="bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TABLE_PAGE_SIZE_OPTIONS.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size} pro Seite
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {vouchersQuery.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : vouchers.length === 0 ? (
            <p className="text-sm text-muted-foreground">Noch keine Gutscheine vorhanden.</p>
          ) : filteredVouchers.length === 0 ? (
            <p className="text-sm text-muted-foreground">Keine Gutscheine für diese Suche gefunden.</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Credits</TableHead>
                    <TableHead>Einlösungen</TableHead>
                    <TableHead>Verbleibend</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Erstellt</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedVouchers.map((voucher) => {
                    const status = voucherStatus(voucher)
                    const deletePending =
                      deleteMutation.isPending &&
                      deleteMutation.variables === voucher.id
                    const canDelete = voucher.redeemed_count === 0

                    return (
                      <TableRow key={voucher.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-medium">{voucher.code}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => copyCode(voucher.code)}
                              title="Code kopieren"
                            >
                              <CopyIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>{formatCreditAmount(voucher.credits)}</TableCell>
                        <TableCell>
                          {voucher.redeemed_count} / {voucher.max_redemptions}
                        </TableCell>
                        <TableCell>{voucher.remaining_redemptions}</TableCell>
                        <TableCell>
                          <Badge variant={status.variant}>{status.label}</Badge>
                        </TableCell>
                        <TableCell>{formatDateTime(voucher.created_at)}</TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => openEditDialog(voucher)}
                            >
                              <PencilIcon className="mr-2 h-4 w-4" />
                              Bearbeiten
                            </Button>
                            {canDelete ? (
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                disabled={deletePending}
                                onClick={() => openDeleteConfirm(voucher)}
                              >
                                {deletePending ? (
                                  <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2Icon className="mr-2 h-4 w-4" />
                                )}
                                Löschen
                              </Button>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>

              <div className="flex flex-col gap-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
                <p>
                  {totalVouchers === 0
                    ? '0 Ergebnisse'
                    : `${(currentPage - 1) * pageSize + 1} - ${Math.min(
                        currentPage * pageSize,
                        totalVouchers,
                      )} von ${totalVouchers}`}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage <= 1}
                  >
                    Zurück
                  </Button>
                  <span>
                    Seite {currentPage} / {totalPages}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage >= totalPages}
                  >
                    Weiter
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
